/**
 * «ARRANGEMENTER»: lista og kalenderen i adressen (sak 229). Et spillested har ingen årgang i
 * brukerflaten, så filteret er en PERIODE: Kommende, Tidligere, Alle eller en egen periode
 * (`from`/`to`), og kalenderen viser én måned (`month`). Sal og status som før. En filtrert liste
 * er en lenke og overlever en omlasting, og ingenting nedstrøms ser en uvalidert verdi.
 */
import { VENUE_NIGHT_STATUSES, type VenueNightStatus } from '@tvitvi/shared';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/u;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/u;

export const EVENTS_VIEWS = ['list', 'calendar'] as const;
export type EventsView = (typeof EVENTS_VIEWS)[number];

export const EVENTS_WHEN = ['upcoming', 'past', 'all', 'period'] as const;
export type EventsWhen = (typeof EVENTS_WHEN)[number];

export interface EventsSearch {
  view?: EventsView;
  when?: EventsWhen;
  /** Egen periode, `YYYY-MM-DD`, bare med `when=period`. */
  from?: string;
  to?: string;
  /** Kalenderens måned, `YYYY-MM`. */
  month?: string;
  roomId?: string;
  status?: VenueNightStatus;
  /** Søket (F35): tittel, artist, arrangør og sal. */
  q?: string;
}

export function validateEventsSearch(search: Record<string, unknown>): EventsSearch {
  const out: EventsSearch = {};
  const view = search['view'];
  if (typeof view === 'string' && (EVENTS_VIEWS as readonly string[]).includes(view)) {
    out.view = view as EventsView;
  }
  const when = search['when'];
  if (typeof when === 'string' && (EVENTS_WHEN as readonly string[]).includes(when)) {
    out.when = when as EventsWhen;
  }
  const from = search['from'];
  if (typeof from === 'string' && DATE.test(from)) out.from = from;
  const to = search['to'];
  if (typeof to === 'string' && DATE.test(to)) out.to = to;
  const month = search['month'];
  if (typeof month === 'string' && MONTH.test(month)) out.month = month;
  const room = search['roomId'];
  if (typeof room === 'string' && UUID.test(room)) out.roomId = room;
  const status = search['status'];
  if (typeof status === 'string' && (VENUE_NIGHT_STATUSES as readonly string[]).includes(status)) {
    out.status = status as VenueNightStatus;
  }
  const q = search['q'];
  if (typeof q === 'string' && q.trim() !== '') out.q = q.trim().slice(0, 200);
  return out;
}

/** Dagen før `day` (`YYYY-MM-DD`). */
export function dayBefore(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (date ?? 1) - 1))
    .toISOString()
    .slice(0, 10);
}

export interface EventsPeriod {
  from?: string;
  to?: string;
  direction: 'asc' | 'desc';
}

/**
 * Hva lista spør API-et om. Kommende er fra og med i dag, eldst først; Tidligere er til og med i
 * går, nyest først (det man leter etter er som regel forrige helg). En egen periode leses som
 * skrevet, og en baklengs periode snus framfor å bli en feil.
 */
export function periodOf(search: EventsSearch, today: string): EventsPeriod {
  switch (search.when ?? 'upcoming') {
    case 'upcoming':
      return { from: today, direction: 'asc' };
    case 'past':
      return { to: dayBefore(today), direction: 'desc' };
    case 'all':
      return { direction: 'asc' };
    case 'period': {
      const { from, to } = search;
      if (from !== undefined && to !== undefined && from > to) {
        return { from: to, to: from, direction: 'asc' };
      }
      return {
        ...(from === undefined ? {} : { from }),
        ...(to === undefined ? {} : { to }),
        direction: 'asc',
      };
    }
  }
}
